import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { formatDate } from '@angular/common';
import SvgPanZoom from 'svg-pan-zoom';

import { EmailThread } from '../../../models/email-thread.model';
import { Point } from '../../../models/point.model';
import { GraphPoint } from '../../../models/graph-point.model';
import * as TreeNodes from '../../../models/tree-nodes';
import { EmailThreadGroupingsService } from '../../../services/email-thread-groupings.service';
// import { FakeEmailThreadGroupingsService } from '../shared/fake-email-thread-groupings.service';
import { HelpService } from '../../../services/help.service';
import { BaseComponent } from '../../base.component';

@Component({
    selector: 'lex-email-thread',
    templateUrl: './email-thread.component.html',
    styleUrls: ['./email-thread.component.scss'],
    standalone: false
})
export class EmailThreadComponent extends BaseComponent implements OnInit {

  maxItems: number = 25;
  graph: GraphPoint[] = [];
  width: number = 150;
  height: number = 60;
  margin: number = 75;
  hoverThread: EmailThread;
  selectedEmails: EmailThread[] = [];
  emailThreadId: string;
  emailDocIds: string[] = [];
  selectedDocIds: string = "";

  get emailBrowseLink(): string {
    let url: string = `/review/browse?EmailThreadId=${this.emailThreadId}`;
    if (this.selectedDocIds && (this.selectedDocIds.length > 0)) {
      url += `&SelectedDocIds=${this.selectedDocIds}`;
    }
    return url;
  }

  onClick(email: EmailThread): void {
    // Add or remove email from array of selected emails (toggle). Only allow X emails based on maxItems docIds.
    const index = this.selectedEmails.indexOf(email, 0);
    if (index > -1) {
      this.selectedEmails.splice(index, 1);
    }
    else if (this.emailDocIds.length < this.maxItems) {
      this.selectedEmails.push(email);
    }

    // Get all the docIds from the selected emails to generate selectedDocIds.
    this.emailDocIds = [];
    this.selectedEmails.forEach((x: EmailThread) => {
      const tempList = this.getAllDocIds(x);
      this.emailDocIds.push(...tempList);
    });

    this.selectedDocIds = "";
    if (this.emailDocIds.length <= this.maxItems) {
      this.selectedDocIds = this.emailDocIds.join(',');
    }
  }

  constructor(private route: ActivatedRoute, private sanitizer: DomSanitizer, private changeDetectRef: ChangeDetectorRef,
      private threadGroupingsService: EmailThreadGroupingsService, private helpService: HelpService) {
      super();
  }

  ngOnInit(): void {
    this.route.params.subscribe(paramsId => {
      this.emailThreadId = paramsId.id;
      this.threadGroupingsService.getEmailThreads(this.emailThreadId).subscribe((threads: EmailThread[]) => {
        this.initializeTree(threads);

        // The SVG must be rendered before calling SvgPanZoom. Otherwise, SvgPanZoom cannot calculate the initial zoom factor.
        // Hence the need to explicitly call detectChanges().
        this.changeDetectRef.detectChanges();
        SvgPanZoom('#emailThread');
      });
    });

    this.helpService.updateHelpLink("browse-menu/email-thread-groups");
  }

  initializeTree(threads: EmailThread[]): void {

    // Add each emailThread as a node on the tree. This assumes no missing emails.
    const tree = new TreeNodes.Tree<EmailThread>();

    threads.forEach((x: EmailThread) => {
      const parent: EmailThread = this.getParent(x, threads);
      if (!parent) tree.addRoot(x)
      else tree.add(x, parent);
    });

    // Create list of boxes and lines between them.
    const queue = [tree.root];
    while (queue.length) {
      const node = queue.shift();

      const lines: [Point, Point][] = [];
      node.children.forEach(child => {
        lines.push([{ x: node.col, y: node.row }, { x: child.col - node.col, y: child.row - node.row }]);
      });

      const graphPoint: GraphPoint = {
        rect: { x: node.col, y: node.row },
        lines: lines,
        threadPresent: (node.data.primeDoc != null),
        thread: node.data
      };

      this.graph.push(graphPoint);
      queue.push(...node.children);
    }
  }

  getFillValue(thread: EmailThread) {
    return thread.primeDoc.coding.hasBeenReviewed ? 'none' : 'green';
  }

  getVisibilityValue(thread: EmailThread) {
    return thread.isUnique ? 'visible' : 'hidden';
  }

  getParent(thread: EmailThread, threads: EmailThread[]): EmailThread {
    if (!thread.parentId) { return null; }
    const parent: EmailThread = threads.find(x => x.id == thread.parentId);
    return parent;
	}

	clickBox(elem: HTMLElement) {
		elem.classList.toggle('email-selected');
	}

  isSelected(thread: EmailThread) {
      const selected: boolean = this.selectedEmails.indexOf(thread, 0) > -1;

      return selected;
  }

  getLineDashes(graphPoint: GraphPoint): string {
    if (graphPoint && graphPoint.threadPresent) {
      return "";
    }
    else {
      return "5";
    }
  }

  getDuplicateIndicator(thread: EmailThread): string {
    if (thread && thread.docIds && (thread.docIds.length > 1)) {
      return thread.docIds.length.toString();
    }
    else {
      return "";
    }
  }

  getDocId(thread: EmailThread): string {
    if (thread && thread.primeDoc) {
      return thread.primeDoc.docId;
    }
    else {
      return "";
    }
  }

  getAllDocIds(thread: EmailThread): string[] {
    if (thread && thread.docIds) {
      return thread.docIds;
    }
    else {
      return [];
    }
  }

  getEmailTitle(thread: EmailThread): string {
    if (thread && thread.primeDoc && thread.primeDoc.common) {
      return thread.primeDoc.common.docTitle;
    }
    else {
      return "";
    }
  }

  getEmailFrom(thread: EmailThread): string {
    if (thread && thread.primeDoc && thread.primeDoc.email) {
      return thread.primeDoc.email.fromField;
    }
    else {
      return "";
    }
  }

  getEmailTo(thread: EmailThread): string {
    if (thread && thread.primeDoc && thread.primeDoc.email) {
      return thread.primeDoc.email.toField;
    }
    else {
      return "";
    }
  }

  getEmailSubject(thread: EmailThread): string {
    if (thread && thread.primeDoc && thread.primeDoc.email) {
      return thread.primeDoc.email.subjectMatter;
    }
    else {
      return "";
    }
  }

  getDateTimeSent(thread: EmailThread): string {
    if (thread && thread.primeDoc && thread.primeDoc.email) {
      return thread.primeDoc.email.dateSent;
    }
    else {
      return "";
    }
  }

  getDateSent(thread: EmailThread): string {
    if (thread && thread.primeDoc && thread.primeDoc.email) {
      return formatDate(thread.primeDoc.email.dateSent, "mediumDate", "en-US", "UTC");
    }
    else {
      return "";
    }
  }

  getTimeSent(thread: EmailThread): string {
    if (thread && thread.primeDoc && thread.primeDoc.email) {
      return formatDate(thread.primeDoc.email.dateSent, "mediumTime", "en-US", "UTC");
    }
    else {
      return "";
    }
  }

}
